<template>
  <container>
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Live Run</h1>
        <p class="pg-title-sub">Monitor active sessions and open the next scheduled LiveRun races</p>
      </div>
      <router-link to="/admin/races/add" class="lr-primary-btn">
        <svg class="lr-btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        New Race
      </router-link>
    </div>

    <div class="lr-summary-grid">
      <div class="lr-summary-card">
        <span class="lr-summary-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 7v5l3 2"/>
          </svg>
        </span>
        <div>
          <span class="lr-summary-label">In Progress</span>
          <strong class="lr-summary-value">{{ racesInProgress.length }}</strong>
        </div>
      </div>
      <div class="lr-summary-card">
        <span class="lr-summary-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
          </svg>
        </span>
        <div>
          <span class="lr-summary-label">To Start</span>
          <strong class="lr-summary-value">{{ racesToStart.length }}</strong>
        </div>
      </div>
      <div class="lr-summary-card">
        <span class="lr-summary-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 6h13M8 12h13M8 18h13"/>
            <path d="M3 6h.01M3 12h.01M3 18h.01"/>
          </svg>
        </span>
        <div>
          <span class="lr-summary-label">Ready Queue</span>
          <strong class="lr-summary-value">{{ totalLiveRaces }}</strong>
        </div>
      </div>
    </div>

    <div class="lr-list-stack">
      <race-list
        subtitle="In Progress"
        description="Live sessions that have started and are awaiting completion"
        :races="racesInProgress"
        :loading="isRacesLoading"
        message-empty-list="No races are currently in progress."
        @delete="handleDeleteRace">
      </race-list>
      <race-list
        subtitle="To Start"
        description="Scheduled sessions ready for trainer launch"
        :races="racesToStart"
        :loading="isRacesLoading"
        message-empty-list="You don't have upcoming races."
        @delete="handleDeleteRace">
      </race-list>
    </div>

    <div v-if="$isDevMode()" class="lr-dev-card lr-dev-card--bottom">
      <div class="lr-dev-copy">
        <strong class="lr-dev-title">Development Tools</strong>
        <span class="lr-dev-subtitle">Remove all visible active and upcoming races from this view.</span>
      </div>
      <button class="lr-danger-btn" @click="deleteAllRaces">
        <svg class="lr-btn-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"/>
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
          <path d="M10 11v6M14 11v6"/>
        </svg>
        Delete All
      </button>
    </div>
  </container>
</template>

<script>
import Container from '@/components/Container'
import RaceList from '@/components/Race/RaceList'
import { mapActions, mapGetters } from 'vuex'
import { clearRememberMeToken } from '@/utils/rememberMeAuth'

export default {
  name: 'live-run',
  data () {
    return {
      racesInProgress: [],
      racesToStart: []
    }
  },
  computed: {
    ...mapGetters({
      trainer: 'Trainer/trainer',
      races: 'RacesByTrainer/races',
      queue: 'LoadingQueue/queue',
      istrainer: 'LoadingQueue/istrainer'
    }),
    isRacesLoading () {
      return this.queue.indexOf('getTrainer') !== -1 || this.queue.indexOf('getRacesByTrainer') !== -1
    },
    totalLiveRaces () {
      return this.racesInProgress.length + this.racesToStart.length
    }
  },
  methods: {
    initialize () {
      this.addTaskToQueue('getTrainer')
      this.addTaskToQueue('getRacesByTrainer')

      this.getTrainer(this.$auth.user().id)
        .then(() => {
          this.removeTaskFromQueue('getTrainer')
        })
        .catch(() => {
         this.removeTaskFromQueue('getTrainer')
         this.removeTaskFromQueue('getRacesByTrainer')
         this.removeTaskFromQueue('getTrainerByUser')
         this.setIsTrainer(false)
         clearRememberMeToken()
         this.$auth.logout({redirect: '/login'})
        })
    },
    getRaces () {
      this.getRacesByTrainer(this.trainer.id)
        .then(() => {
          this.removeTaskFromQueue('getRacesByTrainer')
        })

    },
    setRacesInProgress () {
      this.racesInProgress = this.races.filter((race) => race.startDate !== null && race.finishDate === null)
    },
    setRacesToStart () {
      this.racesToStart = this.races.filter((race) => race.startDate === null && race.finishDate === null)
    },
    ...mapActions({
      getTrainer: 'Trainer/get',
      setIsTrainer: 'Trainer/setIsTrainer',
      getRacesByTrainer: 'RacesByTrainer/get',
      addTaskToQueue: 'LoadingQueue/addTaskToQueue',
      removeTaskFromQueue: 'LoadingQueue/removeTaskFromQueue',
      deleteOne: 'Race/deleteOne'
    }),
    deleteAllRaces () {
      if (!confirm('Dev Mode: Are you sure you want to delete ALL visible races?')) return
      
      const allRaces = [...this.racesInProgress, ...this.racesToStart]
      if (allRaces.length === 0) {
        alert('No races to delete')
        return
      }

      this.addTaskToQueue('deleteAllRaces')
      
      const promises = allRaces.map(race => this.deleteOne(race.id))
      
      Promise.all(promises)
        .then(() => {
          this.getRaces()
          this.removeTaskFromQueue('deleteAllRaces')
        })
        .catch(error => {
          console.error('Error deleting all races:', error)
          this.removeTaskFromQueue('deleteAllRaces')
        })
    },
    handleDeleteRace (raceId) {
       if (!confirm('Dev Mode: Delete this race?')) return
       
       this.addTaskToQueue('deleteRace')
       this.deleteOne(raceId)
        .then(() => {
          this.getRaces()
          this.removeTaskFromQueue('deleteRace')
        })
        .catch(error => {
          console.error('Error deleting race:', error)
          this.removeTaskFromQueue('deleteRace')
        })
    }
  },
  created () {
    this.initialize()
  },
  watch: {
    trainer () {
      this.getRaces()
    },
    races () {
      this.setRacesInProgress()
      this.setRacesToStart()
    }
  },
  components: {
    RaceList,
    Container
  }
}
</script>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');
</style>
<style scoped>
.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.lr-primary-btn,
.lr-danger-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.lr-primary-btn {
  min-height: 36px;
  padding: 0 18px;
  background: #5b4fcf;
  color: #fff;
  border: none;
}

.lr-primary-btn:hover {
  background: #4338ca;
  color: #fff;
}

.lr-danger-btn {
  height: 33px;
  padding: 0 14px;
  background: #fff;
  color: #c0392b;
  border: 1.5px solid #f0c8c2;
  cursor: pointer;
}

.lr-danger-btn:hover {
  background: #fff4f2;
  border-color: #c0392b;
}

.lr-btn-icon {
  flex: 0 0 auto;
}

.lr-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.lr-summary-card,
.lr-dev-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.lr-summary-card {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lr-summary-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.lr-summary-label {
  display: block;
  font-size: 12px;
  color: #8a8a9a;
  font-weight: 600;
  margin-bottom: 3px;
}

.lr-summary-value {
  display: block;
  font-size: 22px;
  line-height: 1;
  color: #1a1a2e;
  font-weight: 800;
}

.lr-dev-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.lr-dev-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lr-dev-title {
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 700;
}

.lr-dev-subtitle {
  color: #8a8a9a;
  font-size: 12px;
}

.lr-list-stack {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

@media (max-width: 760px) {
  .pg-title-row,
  .lr-dev-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .lr-summary-grid {
    grid-template-columns: 1fr;
  }

  .lr-primary-btn,
  .lr-danger-btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// LiveRun.vue?f24ec0b2